import React, { useState } from "react";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import "./HaqtDateRangePicker.scss";

import { DateRangePicker } from "react-date-range";
import { Box, Button, TextField, useMediaQuery } from "@mui/material";
import { CalendarMonthTwoTone } from "@mui/icons-material";
import { format } from "date-fns";

import {
  addDays,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfWeek,
  endOfWeek,
  isSameDay,
  differenceInCalendarDays,
} from "date-fns";
const defineds = {
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfToday: startOfDay(new Date()),
  endOfToday: endOfDay(new Date()),
  startOfYesterday: startOfDay(addDays(new Date(), -1)),
  endOfYesterday: endOfDay(addDays(new Date(), -1)),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
  endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
};

const staticRangeHandler = {
  range: {},
  isSelected(range) {
    const definedRange = this.range();
    return (
      isSameDay(range.startDate, definedRange.startDate) &&
      isSameDay(range.endDate, definedRange.endDate)
    );
  },
};

export const createStaticRanges = (ranges) => {
  return ranges.map((range) => ({ ...staticRangeHandler, ...range }));
};

const defaultStaticRanges = createStaticRanges([
  {
    label: "Today",
    range: () => ({
      startDate: defineds.startOfToday,
      endDate: defineds.endOfToday,
    }),
  },
  {
    label: "This Week",
    range: () => ({
      startDate: defineds.startOfWeek,
      endDate: defineds.endOfWeek,
    }),
  },
  {
    label: "This Month",
    range: () => ({
      startDate: startOfMonth(new Date()),
      endDate: endOfMonth(new Date()),
    }),
  },
]);

const HaqtDateRangePicker = ({ onApply, hideYear, onError, placeholder }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectionRange, setSelectionRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");
  const errorMsg = "Please enter valid date-range.";

  //below method will give the date format in mm/dd/yyyy
  const formatDate = (date) => {
    if (date) {
      let res = format(new Date(date), "MM/dd/yyyy");
      return res;
    } else {
      return "";
    }
  };

  //formattedDate will give the fromDate - toDate example:- (02/01/2024 - 03/28/2024)
  const formattedDate =
    formatDate(selectionRange[0].startDate) +
    " - " +
    formatDate(selectionRange[0].endDate);

  const toggleDatePicker = () => {
    setShowDateRangePicker(!showDateRangePicker);
  };

  //date validations are done on Manual Date Entry
  const handleChange = (event) => {
    const enteredValue = event.target.value;
    setInputValue(enteredValue);
    if (!inputValue) return;
    const value = enteredValue.replace(/\s+/g, ""); // Remove leading and trailing spaces
    // Regular expression to match the desired date format
    const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}\s*-\s*\d{2}\/\d{2}\/\d{4}$/;

    const [startDateString, endDateString] = value.split("-");
    const [startMonth, startDay, startYear] = (startDateString ?? "").split(
      "/",
    );
    const [endMonth, endDay, endYear] = (endDateString ?? "").split("/");

    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);

    if (
      !dateFormatRegex.test(value) ||
      isNaN(startDate.getTime()) ||
      isNaN(endDate.getTime()) ||
      startDate >= endDate ||
      startDate.getFullYear() !== parseInt(startYear, 10) ||
      endDate.getFullYear() !== parseInt(endYear, 10) ||
      startDate.getMonth() + 1 !== parseInt(startMonth, 10) ||
      endDate.getMonth() + 1 !== parseInt(endMonth, 10) ||
      startDate.getDate() !== parseInt(startDay, 10) ||
      endDate.getDate() !== parseInt(endDay, 10)
    ) {
      setError(errorMsg);
      onError && onError(errorMsg);
    } else {
      setError("");
      onError && onError("");
    }
  };

  //onClick of Apply button
  const onApplyClicked = () => {
    onApply(selectionRange);
    setInputValue(formattedDate);
    setShowDateRangePicker(false);
    setError("");
  };

  return (
    <div className="haqt-date-picker">
      <TextField
        variant="outlined"
        value={inputValue}
        onChange={handleChange}
        placeholder={placeholder}
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: (
            <CalendarMonthTwoTone
              color="action"
              style={{ cursor: "pointer" }}
              onClick={toggleDatePicker}
            />
          ),
        }}
      />
      {showDateRangePicker && (
        <Box className="date-range-wrapper">
          <div className="date-range-picker">
            <DateRangePicker
              editableDateInputs={false}
              months={isMobile ? 1 : 2}
              direction="horizontal"
              ranges={selectionRange}
              onChange={(range) => setSelectionRange([range.selection])}
              color="red"
              fixedHeight={true}
              inputRanges={[]}
              staticRanges={isMobile ? [] : defaultStaticRanges}
              rangeColors={["#0A6073"]}
            />
          </div>
          <Button className="apply-btn" onClick={onApplyClicked}>
            Apply
          </Button>
        </Box>
      )}
    </div>
  );
};

export default HaqtDateRangePicker;
